import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuCalendar,
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuClipboardList,
  LuClock3,
  LuEye,
  LuFilter,
  LuHourglass,
  LuPaperclip,
  LuRefreshCw,
  LuSearch,
  LuX,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  TICKET_CATEGORY_OPTIONS,
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
  fetchSupportAssignedTicketStats,
  fetchSupportAssignedTickets,
  fetchSupportTicketById,
  ticketExtractMessage,
} from "../../services/supportTicket.service";
import "../../assets/styles/support_disputes.scss";

const PAGE_OPTIONS = [10, 20, 50];

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function truncateText(value, max = 28) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function nameInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function priorityClass(priority) {
  const p = String(priority || "").toLowerCase();
  if (p === "urgent" || p === "critical") return "sd-pill sd-priority sd-priority--urgent";
  if (p === "high") return "sd-pill sd-priority sd-priority--high";
  if (p === "medium") return "sd-pill sd-priority sd-priority--medium";
  return "sd-pill sd-priority sd-priority--low";
}

function statusClass(status) {
  return `sd-pill sd-status sd-status--${String(status || "").replace(/\s+/g, "_").toLowerCase()}`;
}

function formatStatus(status, label) {
  if (label) return label;
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 5) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

const emptyFilters = {
  query: "",
  status: "",
  priority: "",
  categoryCode: "",
  customer: "",
  dateFrom: "",
  dateTo: "",
  dueToday: false,
  overdue: false,
};

export default function SupportAssignedTicketsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState({
    ...emptyFilters,
    status: searchParams.get("status") || "",
    priority: searchParams.get("priority") || "",
    categoryCode: searchParams.get("categoryCode") || "",
    dueToday: searchParams.get("dueToday") === "1",
    overdue: searchParams.get("overdue") === "1",
  });
  const [applied, setApplied] = useState(draft);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  });
  const [quickView, setQuickView] = useState(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewError, setQuickViewError] = useState("");

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchSupportAssignedTicketStats();
      setStats({
        total: Math.trunc(Number(data.total) || 0),
        inProgress: Math.trunc(Number(data.inProgress) || 0),
        resolved: Math.trunc(Number(data.resolved) || 0),
        closed: Math.trunc(Number(data.closed) || 0),
      });
    } catch {
      // Keep prior stats.
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSupportAssignedTickets({
        search: applied.query.trim() || undefined,
        status: applied.status || undefined,
        priority: applied.priority || undefined,
        categoryCode: applied.categoryCode || undefined,
        customer: applied.customer.trim() || undefined,
        dateFrom: applied.dateFrom || undefined,
        dateTo: applied.dateTo || undefined,
        dueToday: applied.dueToday ? "1" : undefined,
        overdue: applied.overdue ? "1" : undefined,
        page,
        pageSize,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Math.trunc(Number(data.total) || 0));
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to load assigned tickets."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [applied, page, pageSize]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const updateDraft = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const applyFilters = () => {
    setApplied({ ...draft });
    setPage(1);
    const params = new URLSearchParams();
    if (draft.status) params.set("status", draft.status);
    if (draft.priority) params.set("priority", draft.priority);
    if (draft.categoryCode) params.set("categoryCode", draft.categoryCode);
    if (draft.dueToday) params.set("dueToday", "1");
    if (draft.overdue) params.set("overdue", "1");
    setSearchParams(params, { replace: true });
  };

  const resetFilters = () => {
    setDraft({ ...emptyFilters });
    setApplied({ ...emptyFilters });
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const refreshAll = async () => {
    await Promise.all([load(), loadStats()]);
  };

  const openDetail = (ticketId) => {
    navigate(`/dashboard/support/tickets/${ticketId}`);
  };

  const openQuickView = async (row) => {
    const ticketId = Number(row?.id);
    if (!Number.isFinite(ticketId) || ticketId <= 0) return;
    setQuickView({ ticket: row, attachments: [] });
    setQuickViewError("");
    setQuickViewLoading(true);
    try {
      const detail = await fetchSupportTicketById(ticketId);
      if (detail?.ticket) {
        setQuickView(detail);
      } else {
        setQuickViewError("Ticket details are unavailable.");
      }
    } catch (error) {
      setQuickViewError(ticketExtractMessage(error, "Failed to load ticket details."));
    } finally {
      setQuickViewLoading(false);
    }
  };

  const closeQuickView = () => {
    setQuickView(null);
    setQuickViewError("");
    setQuickViewLoading(false);
  };

  const quickTicket = quickView?.ticket || null;
  const quickAttachment = Array.isArray(quickView?.attachments) ? quickView.attachments[0] : null;

  return (
    <div className="sd-page sd-list-page">
      <header className="sd-head">
        <div>
          <nav className="sd-breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">My Assigned Tickets</span>
          </nav>
          <h1>My Assigned Tickets</h1>
          <p>Manage tickets assigned to you — update status, reply to customers, and track SLA.</p>
        </div>
        <button type="button" className="sd-btn sd-btn--ghost" onClick={refreshAll}>
          <LuRefreshCw aria-hidden /> Refresh
        </button>
      </header>

      <section className="sd-stats">
        <article className="sd-stat">
          <span className="sd-stat__icon sd-stat__icon--blue">
            <LuClipboardList aria-hidden />
          </span>
          <div className="sd-stat__body">
            <div className="sd-stat__value">{stats.total.toLocaleString()}</div>
            <div className="sd-stat__label">Total Assigned</div>
            <div className="sd-stat__hint">All time</div>
          </div>
        </article>
        <article className="sd-stat">
          <span className="sd-stat__icon sd-stat__icon--orange">
            <LuHourglass aria-hidden />
          </span>
          <div className="sd-stat__body">
            <div className="sd-stat__value">{stats.inProgress.toLocaleString()}</div>
            <div className="sd-stat__label">In Progress</div>
            <div className="sd-stat__hint">Active tickets</div>
          </div>
        </article>
        <article className="sd-stat">
          <span className="sd-stat__icon sd-stat__icon--green">
            <LuCircleCheck aria-hidden />
          </span>
          <div className="sd-stat__body">
            <div className="sd-stat__value">{stats.resolved.toLocaleString()}</div>
            <div className="sd-stat__label">Resolved</div>
            <div className="sd-stat__hint">Tickets resolved</div>
          </div>
        </article>
        <article className="sd-stat">
          <span className="sd-stat__icon sd-stat__icon--purple">
            <LuClock3 aria-hidden />
          </span>
          <div className="sd-stat__body">
            <div className="sd-stat__value">{stats.closed.toLocaleString()}</div>
            <div className="sd-stat__label">Closed</div>
            <div className="sd-stat__hint">Tickets closed</div>
          </div>
        </article>
      </section>

      <section className="sd-filters" aria-label="Filters">
        <div className="sd-filters__title">
          <LuFilter aria-hidden /> Filters
        </div>
        <div className="sd-filters__row">
          <label className="sd-search">
            <LuSearch aria-hidden />
            <input
              value={draft.query}
              onChange={(e) => updateDraft({ query: e.target.value })}
              placeholder="Search by Ticket ID, Customer, Subject..."
            />
          </label>
          <select
            value={draft.status}
            onChange={(e) => updateDraft({ status: e.target.value })}
          >
            {TICKET_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || "all-status"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={draft.priority}
            onChange={(e) => updateDraft({ priority: e.target.value })}
          >
            {TICKET_PRIORITY_OPTIONS.map((opt) => (
              <option key={opt.value || "all-priority"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={draft.categoryCode}
            onChange={(e) => updateDraft({ categoryCode: e.target.value })}
          >
            {TICKET_CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value || "all-category"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={draft.customer}
            onChange={(e) => updateDraft({ customer: e.target.value })}
            placeholder="Customer name"
          />
        </div>
        <div className="sd-filters__row sd-filters__row--secondary">
          <label className="sd-date">
            <LuCalendar aria-hidden />
            <input
              type="date"
              value={draft.dateFrom}
              onChange={(e) => updateDraft({ dateFrom: e.target.value })}
              aria-label="Start date"
            />
          </label>
          <label className="sd-date">
            <LuCalendar aria-hidden />
            <input
              type="date"
              value={draft.dateTo}
              onChange={(e) => updateDraft({ dateTo: e.target.value })}
              aria-label="End date"
            />
          </label>
          <label className="sd-check">
            <input
              type="checkbox"
              checked={draft.dueToday}
              onChange={(e) =>
                updateDraft({
                  dueToday: e.target.checked,
                  overdue: e.target.checked ? false : draft.overdue,
                })
              }
            />
            Due Today
          </label>
          <label className="sd-check">
            <input
              type="checkbox"
              checked={draft.overdue}
              onChange={(e) =>
                updateDraft({
                  overdue: e.target.checked,
                  dueToday: e.target.checked ? false : draft.dueToday,
                })
              }
            />
            Overdue
          </label>
          <div className="sd-filters__actions">
            <button type="button" className="sd-btn sd-btn--ghost" onClick={resetFilters}>
              Reset
            </button>
            <button type="button" className="sd-btn sd-btn--primary" onClick={applyFilters}>
              Apply Filters
            </button>
          </div>
        </div>
      </section>

      <section className="sd-card">
        <div className="sd-table-wrap">
          <table className="sd-table sd-table--assigned">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Subject</th>
                <th>Customer</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>SLA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="sd-empty">
                    Loading assigned tickets...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="sd-empty">
                    No assigned tickets found.
                  </td>
                </tr>
              ) : (
                items.map((ticket) => (
                  <tr key={ticket.id} className={ticket.slaOverdue ? "is-overdue" : ""}>
                    <td>
                      <button
                        type="button"
                        className="sd-id-link"
                        onClick={() => openQuickView(ticket)}
                      >
                        {ticket.ticketNumber}
                      </button>
                    </td>
                    <td>
                      <div className="sd-stack">
                        <strong title={ticket.subject || undefined}>
                          {truncateText(ticket.subject || "—", 28)}
                        </strong>
                        <span title={ticket.referenceNumber || undefined}>
                          {truncateText(ticket.referenceNumber || ticket.affectedModule || "—", 20)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="sd-person">
                        <span className="sd-avatar">{nameInitials(ticket.customerName)}</span>
                        <div className="sd-stack">
                          <strong title={ticket.customerName}>
                            {truncateText(ticket.customerName, 14)}
                          </strong>
                          <span title={ticket.customerEmail || undefined}>
                            {truncateText(ticket.customerEmail || "—", 16)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="sd-cell-text" title={ticket.categoryLabel}>
                        {truncateText(ticket.categoryLabel, 22)}
                      </span>
                    </td>
                    <td>
                      <span className={priorityClass(ticket.priority)}>
                        {String(ticket.priority).toLowerCase() === "urgent"
                          ? "Urgent"
                          : ticket.priority}
                      </span>
                    </td>
                    <td>
                      <span className={statusClass(ticket.status)}>
                        {formatStatus(ticket.status, ticket.statusLabel)}
                      </span>
                    </td>
                    <td>
                      <div className="sd-stack">
                        <strong>{formatDate(ticket.createdAt)}</strong>
                        <span>{formatTime(ticket.createdAt)}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={
                          ticket.slaOverdue
                            ? "sd-sla sd-sla--overdue"
                            : ticket.slaDueToday
                              ? "sd-sla sd-sla--soon"
                              : "sd-sla"
                        }
                      >
                        {ticket.slaRemainingLabel || "—"}
                      </span>
                    </td>
                    <td className="sd-actions-cell">
                      <button
                        type="button"
                        className="sd-icon-btn sd-icon-btn--ghost"
                        title="View details"
                        onClick={() => openDetail(ticket.id)}
                      >
                        <LuEye aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <footer className="sd-pagination">
          <div>
            Showing {items.length ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, total)} of {total} tickets
          </div>
          <div className="sd-pagination__controls">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
            <button
              type="button"
              className="sd-icon-btn"
              disabled={currentPage <= 1}
              onClick={() => setPage((v) => Math.max(1, v - 1))}
              aria-label="Previous page"
            >
              <LuChevronLeft aria-hidden />
            </button>
            {pageNumbers.map((item) => (
              <button
                key={item}
                type="button"
                className={`sd-page-btn${currentPage === item ? " is-active" : ""}`}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              className="sd-icon-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((v) => Math.min(totalPages, v + 1))}
              aria-label="Next page"
            >
              <LuChevronRight aria-hidden />
            </button>
          </div>
        </footer>
      </section>

      {quickTicket
        ? createPortal(
            <div
              className="sd-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sd-ticket-quick-view-title"
              onClick={closeQuickView}
            >
              <div
                className="sd-modal__panel sd-quick-view"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="sd-quick-view__head">
                  <div className="sd-quick-view__head-main">
                    <div className="sd-quick-view__title-row">
                      <h2 id="sd-ticket-quick-view-title">{quickTicket.ticketNumber}</h2>
                      <span className="sd-quick-view__id-chip">
                        #{String(quickTicket.id).padStart(6, "0")}
                      </span>
                    </div>
                    <p className="sd-quick-view__subject">
                      {quickTicket.subject || "Ticket quick view"}
                    </p>
                    <div className="sd-quick-view__badges">
                      <span className={priorityClass(quickTicket.priority)}>
                        {String(quickTicket.priority).toLowerCase() === "urgent"
                          ? "Urgent"
                          : quickTicket.priority || "—"}
                      </span>
                      <span className={statusClass(quickTicket.status)}>
                        {formatStatus(quickTicket.status, quickTicket.statusLabel)}
                      </span>
                      {quickTicket.slaRemainingLabel ? (
                        <span
                          className={
                            quickTicket.slaOverdue
                              ? "sd-sla sd-sla--overdue"
                              : quickTicket.slaDueToday
                                ? "sd-sla sd-sla--soon"
                                : "sd-sla"
                          }
                        >
                          SLA: {quickTicket.slaRemainingLabel}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sd-icon-btn sd-icon-btn--ghost"
                    aria-label="Close"
                    onClick={closeQuickView}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="sd-quick-view__body">
                  {quickViewLoading ? (
                    <p className="sd-quick-view__loading">Loading ticket details...</p>
                  ) : null}
                  {quickViewError ? (
                    <p className="sd-quick-view__error">{quickViewError}</p>
                  ) : null}

                  <section className="sd-quick-view__section">
                    <h3>Ticket Details</h3>
                    <div className="sd-quick-view__fields">
                      <div className="sd-quick-view__field">
                        <span>Category</span>
                        <strong>{quickTicket.categoryLabel || "—"}</strong>
                      </div>
                      <div className="sd-quick-view__field">
                        <span>Affected Module</span>
                        <strong>{quickTicket.affectedModule || "—"}</strong>
                      </div>
                      <div className="sd-quick-view__field">
                        <span>Reference</span>
                        <strong>{quickTicket.referenceNumber || "—"}</strong>
                      </div>
                      <div className="sd-quick-view__field">
                        <span>Submitted</span>
                        <strong>{formatDateTime(quickTicket.createdAt)}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="sd-quick-view__section">
                    <h3>Description</h3>
                    <p className="sd-quick-view__description">
                      {quickTicket.description || "No description provided."}
                    </p>
                  </section>

                  <section className="sd-quick-view__section">
                    <h3>Attachment</h3>
                    {quickAttachment?.filePath || quickAttachment?.fileName ? (
                      <a
                        className="sd-quick-view__attachment"
                        href={resolvePublicFileUrl(quickAttachment.filePath)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="sd-quick-view__attachment-icon" aria-hidden>
                          <LuPaperclip />
                        </span>
                        <span>
                          <strong>{quickAttachment.fileName || "View attachment"}</strong>
                          <small>Open / download file</small>
                        </span>
                      </a>
                    ) : (
                      <p className="sd-quick-view__description sd-quick-view__description--muted">
                        No attachment submitted with this ticket.
                      </p>
                    )}
                  </section>

                  <section className="sd-quick-view__section">
                    <h3>Customer</h3>
                    <div className="sd-quick-view__parties">
                      <article className="sd-quick-view__party">
                        <span className="sd-quick-view__party-label">Customer</span>
                        <div className="sd-person">
                          <span className="sd-avatar">
                            {nameInitials(quickTicket.customerName)}
                          </span>
                          <div className="sd-stack">
                            <strong>{quickTicket.customerName || "—"}</strong>
                            <span>{quickTicket.customerEmail || "—"}</span>
                            {quickTicket.customerPhone ? (
                              <span>
                                {quickTicket.customerCountryCode
                                  ? `${quickTicket.customerCountryCode} `
                                  : ""}
                                {quickTicket.customerPhone}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    </div>
                  </section>
                </div>

                <footer className="sd-quick-view__footer">
                  <button
                    type="button"
                    className="sd-btn sd-btn--ghost"
                    onClick={closeQuickView}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="sd-btn sd-btn--primary"
                    onClick={() => {
                      closeQuickView();
                      openDetail(quickTicket.id);
                    }}
                  >
                    Open full details
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
